import { create } from 'zustand'
import { groupsApi } from '../api/groups'
import type {
  StudentGroup,
  GroupFilters,
  CreateGroupData,
  UpdateGroupData,
  AddMemberData,
} from '../types'

interface GroupState {
  groups: StudentGroup[]
  currentGroup: StudentGroup | null
  isLoading: boolean
  error: string | null
  pagination: {
    currentPage: number
    lastPage: number
    total: number
    per_page: number
  }
  filters: GroupFilters

  // Actions
  fetchGroups: (filters?: GroupFilters) => Promise<void>
  fetchGroup: (id: number) => Promise<void>
  createGroup: (data: CreateGroupData) => Promise<StudentGroup>
  updateGroup: (id: number, data: UpdateGroupData) => Promise<void>
  deleteGroup: (id: number) => Promise<void>
  addMember: (groupId: number, data: AddMemberData) => Promise<void>
  removeMember: (groupId: number, studentId: number) => Promise<void>
  archiveGroup: (id: number) => Promise<void>
  restoreGroup: (id: number) => Promise<void>
  setFilters: (filters: GroupFilters) => void
  clearError: () => void
}

export const useGroupStore = create<GroupState>((set, get) => ({
  groups: [],
  currentGroup: null,
  isLoading: false,
  error: null,
  pagination: {
    currentPage: 1,
    lastPage: 1,
    total: 0,
    per_page: 15,
  },
  filters: {},

  fetchGroups: async (filters) => {
    set({ isLoading: true, error: null })
    try {
      const mergedFilters = { ...get().filters, ...filters }
      const response = await groupsApi.getAll(mergedFilters)
      set({
        groups: response.data,
        pagination: {
          currentPage: response.current_page,
          lastPage: response.last_page,
          total: response.total,
          per_page: response.per_page ?? get().pagination.per_page,
        },
        filters: mergedFilters,
      })
    } catch (error: unknown) {
      const err = error as { message?: string }
      set({ error: err.message || 'Ошибка загрузки групп' })
    } finally {
      set({ isLoading: false })
    }
  },

  fetchGroup: async (id) => {
    set({ isLoading: true, error: null })
    try {
      const group = await groupsApi.getById(id)
      set({ currentGroup: group })
    } catch (error: unknown) {
      const err = error as { message?: string }
      set({ error: err.message || 'Ошибка загрузки группы' })
    } finally {
      set({ isLoading: false })
    }
  },

  // Ошибку создания в общую ошибку страницы не пишем: её показывает форма
  // создания в модалке, поверх страницы (в т.ч. 422 с отклонёнными учениками)
  createGroup: async (data) => {
    set({ isLoading: true, error: null })
    try {
      const newGroup = await groupsApi.create(data)
      set((state) => ({ groups: [newGroup, ...state.groups] }))
      return newGroup
    } finally {
      set({ isLoading: false })
    }
  },

  updateGroup: async (id, data) => {
    set({ isLoading: true, error: null })
    try {
      const updatedGroup = await groupsApi.update(id, data)
      set((state) => ({
        groups: state.groups.map((g) => (g.id === id ? updatedGroup : g)),
        currentGroup: state.currentGroup?.id === id ? updatedGroup : state.currentGroup,
      }))
    } catch (error: unknown) {
      const err = error as { message?: string }
      set({ error: err.message || 'Ошибка обновления группы' })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  deleteGroup: async (id) => {
    set({ isLoading: true, error: null })
    try {
      await groupsApi.delete(id)
      set((state) => ({
        groups: state.groups.filter((g) => g.id !== id),
        currentGroup: state.currentGroup?.id === id ? null : state.currentGroup,
      }))
    } catch (error: unknown) {
      const err = error as { message?: string }
      set({ error: err.message || 'Ошибка удаления группы' })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  // Без isLoading и без общей ошибки страницы: добавление идёт из модалки,
  // которая сама показывает ход и ошибку (в т.ч. 422 с отклонёнными
  // учениками). isLoading перерисовал бы карточку группы скелетоном и
  // размонтировал модалку вместе с выбором и текстом ошибки.
  addMember: async (groupId, data) => {
    const updatedGroup = await groupsApi.addMember(groupId, data)
    set((state) => ({
      groups: state.groups.map((g) => (g.id === groupId ? updatedGroup : g)),
      currentGroup: state.currentGroup?.id === groupId ? updatedGroup : state.currentGroup,
    }))
  },

  removeMember: async (groupId, studentId) => {
    set({ isLoading: true, error: null })
    try {
      const updatedGroup = await groupsApi.removeMember(groupId, studentId)
      set((state) => ({
        groups: state.groups.map((g) => (g.id === groupId ? updatedGroup : g)),
        currentGroup: state.currentGroup?.id === groupId ? updatedGroup : state.currentGroup,
      }))
    } catch (error: unknown) {
      const err = error as { message?: string }
      set({ error: err.message || 'Ошибка удаления участника' })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  archiveGroup: async (id) => {
    set({ isLoading: true, error: null })
    try {
      const updatedGroup = await groupsApi.update(id, { status: 'archived' })
      set((state) => ({
        groups: state.groups.map((g) => (g.id === id ? updatedGroup : g)),
        currentGroup: state.currentGroup?.id === id ? updatedGroup : state.currentGroup,
      }))
    } catch (error: unknown) {
      const err = error as { message?: string }
      set({ error: err.message || 'Ошибка архивирования группы' })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  restoreGroup: async (id) => {
    set({ isLoading: true, error: null })
    try {
      const updatedGroup = await groupsApi.update(id, { status: 'active' })
      set((state) => ({
        groups: state.groups.map((g) => (g.id === id ? updatedGroup : g)),
        currentGroup: state.currentGroup?.id === id ? updatedGroup : state.currentGroup,
      }))
    } catch (error: unknown) {
      const err = error as { message?: string }
      set({ error: err.message || 'Ошибка восстановления группы' })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  setFilters: (filters) => set({ filters }),
  clearError: () => set({ error: null }),
}))
